import React from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { FaUserCircle } from 'react-icons/fa';

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 8px 12px;
  border-radius: 40px;
  background: ${props => props.$isOpen ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 6px 10px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 4px 8px;
  }
`;

const AvatarIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.blueLight}, ${colors.blueDark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  ${UserInfoContainer}:hover & {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 38px;
    height: 38px;
    font-size: 20px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 24px;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;

  // Esconde em telas muito pequenas apenas se a prop hideOnMobile for true
  @media (max-width: 480px) {
    display: ${props => props.$hideOnMobile ? 'none' : 'flex'};
  }
`;

const UserName = styled.span`
  color: ${colors.white};
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  max-width: min(100%, 260px);
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
  line-height: 1.3;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 14px;
  }

  // Nome longo não pode empurrar o título do Header para várias linhas
  @media (max-width: 640px) {
    max-width: min(100%, 160px);
  }
`;

const UserCPF = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.2px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 11px;
  }
`;

const ChevronIcon = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  margin-left: 4px;

  @media (max-width: 480px) {
    display: none;
  }
`;

const UserInfo = ({ showDetails = true, isOpen = false, onClick, hideOnMobile = false, isInMenu = false }) => {
  const { user } = useAuth();

  const formatCPF = (cpf) => {
    if (!cpf) return '';
    const numeros = cpf.toString().replace(/\D/g, '');
    if (numeros.length !== 11) return cpf;
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  if (!user) return null;

  return (
    <UserInfoContainer onClick={onClick} $isOpen={isOpen}>
      <AvatarIcon>
        <FaUserCircle size="100%" />
      </AvatarIcon>
      
      {showDetails && (
        <UserDetails $hideOnMobile={hideOnMobile}>
          <UserName style={{ color: isInMenu ? colors.darkBlue : colors.white }}>
            {user.nome || 'Usuário'}
          </UserName>
          <UserCPF style={{ color: isInMenu ? '#64748b' : 'rgba(255, 255, 255, 0.7)' }}>
            {formatCPF(user.documento)}
          </UserCPF>
        </UserDetails>
      )}
      
      {onClick && <ChevronIcon $isOpen={isOpen}>⌵</ChevronIcon>}
    </UserInfoContainer>
  );
};

export default UserInfo;